import React from 'react';
import { useParams } from 'react-router-dom';
import { services } from '../data/services';
import { CTAButton } from '../components/common/CTAButton';
import { BackButton } from '../components/common/BackButton';
import { Check } from 'lucide-react';

export function ServiceDetailsPage() {
  const { id } = useParams();
  const service = services.find(s => s.id === id);

  if (!service) {
    return (
      <div className="min-h-screen pt-32 px-4">
        <div className="max-w-7xl mx-auto">
          <BackButton label="Back to Services" />
          <div className="text-center">
            <h1 className="text-3xl font-bold text-white mb-4">Service Not Found</h1>
            <CTAButton href="/services" variant="secondary">
              View All Services
            </CTAButton>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-32 px-4 pb-16">
      <div className="max-w-7xl mx-auto">
        <BackButton label="Back to Services" />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
          <div>
            <img
              src={service.image}
              alt={service.name}
              className="w-full h-[400px] object-cover rounded-lg"
            />
          </div>

          <div>
            <h1 className="text-4xl font-bold text-white mb-4">{service.name}</h1>
            <p className="text-xl text-gray-300 mb-8">{service.description}</p>

            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4">What You Get</h2>
              <ul className="space-y-3">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="w-5 h-5 text-green-500 mr-3 mt-1" />
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <CTAButton
              href="/book-audit"
              variant="primary"
              icon
            >
              Book a Consultation
            </CTAButton>
          </div>
        </div>

        {/* Additional Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="bg-black/30 border border-blue-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-6">How It Works</h2>
            <p className="text-gray-300 leading-relaxed">
              {service.id === 'chatbots-phone-agents' &&
                'We build custom conversational AI that integrates with your existing systems. The bot learns your business, handles common questions, qualifies leads, and can even book appointments - all while maintaining your brand voice and tone.'}
              {service.id === 'websites-landing-pages' &&
                'We design and develop fast, mobile-responsive sites built specifically for local service businesses. Clean layouts, clear messaging, strong calls-to-action, and easy-to-use forms that actually convert visitors into clients.'}
              {service.id === 'custom-automations' &&
                'We connect your existing tools (booking software, CRM, email, SMS, payment processors) so they work together automatically. Tasks that used to require manual data entry or copying information between systems now happen instantly in the background.'}
            </p>
          </div>

          <div className="bg-black/30 border border-blue-500/20 rounded-lg p-8">
            <h2 className="text-2xl font-bold text-white mb-6">Implementation Process</h2>
            <ul className="space-y-4">
              <li className="flex items-start">
                <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-300">Initial consultation to understand your needs</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-300">Custom build and configuration</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-300">Testing and refinement</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-300">Training and handoff</span>
              </li>
              <li className="flex items-start">
                <Check className="w-5 h-5 text-blue-500 mr-3 mt-1 flex-shrink-0" />
                <span className="text-gray-300">Ongoing support available</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}